















































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import List from '@/components/util/List.vue';
import {
  Part,
  PartsApi,
  PartPagedCollectionApiResponse,
  ApiPartsGetRequest,
  Workorder
} from 'performo-api';
import { mapGetters } from 'vuex';

import { SortChangedEvent } from '@/models/SortChangedEvent';
import { handleApiError } from '@/ApiHelpers';

@Component({
  components: {
    List
  },
  computed: mapGetters({
    api: 'partsApi'
  })
})
export default class PartPicker extends Vue {
  @Prop()
  picks!: Part[];

  @Prop()
  placeholder?: string;

  @Prop({ required: true })
  private order!: Workorder;

  private api!: PartsApi;

  private internalTake = 25;

  private desktopColumnSetup = [
    {
      displayName: 'Code',
      columnName: 'partCode',
      width: '75px',
      dontSort: true
    },
    {
      displayName: 'Name',
      columnName: 'partName',
      width: '125px',
      dontSort: true
    },
    {
      displayName: 'Quantity',
      columnName: 'changeQuantityAction',
      width: '75px',
      dontSort: true
    },
    {
      displayName: 'For Asset',
      columnName: 'selectAssetAction',
      width: '125px',
      dontSort: true
    },
    {
      displayName: '',
      columnName: 'removePartAction',
      width: '50px',
      dontSort: true
    }
  ];

  private mobileColumnSetup = [
    {
      displayName: 'Code',
      columnName: 'partCode',
      width: '75px',
      dontSort: true
    },
    {
      displayName: 'Quantity',
      columnName: 'changeQuantityAction',
      width: '75px',
      dontSort: true
    },
    {
      displayName: 'For Asset',
      columnName: 'selectAssetAction',
      width: '125px',
      dontSort: true
    },
    {
      displayName: '',
      columnName: 'removePartAction',
      width: '50px',
      dontSort: true
    }
  ];

  private cols = [
    { displayName: 'Part Code', columnName: 'partCode', width: 'auto' },
    { displayName: 'Name', columnName: 'partName', width: '225px' },
    { displayName: 'Type', columnName: 'partType', width: 'auto' },
    { displayName: 'Supplier', columnName: 'vendorName', width: 'auto' },
    {
      displayName: 'Unit Cost',
      columnName: 'partUnitCost',
      width: 'auto',
      formatKind: 4
    },
    { displayName: 'Selected', columnName: 'selected', width: '80px' }
  ];

  private pageNumber = 1;

  get parts(): Part[] {
    if (this.pagedParts) {
      return this.pagedParts.data || [];
    }
    return [];
  }

  private autoCompleteQuery = '';
  private autoCompleteParts!: PartPagedCollectionApiResponse;
  private pagedParts!: PartPagedCollectionApiResponse;
  private qtyInput!: number;

  private showModal = false;

  private picksContainsPart(part: Part) {
    if (!this.picks) return false;
    const contains = this.picks.findIndex((f) => f.partId === part.partId) >= 0;
    return contains;
  }

  private query!: ApiPartsGetRequest;

  private itemClicked(item: Part) {
    const pickedIndex = this.picks.findIndex((f) => f.partId == item.partId);
    if (pickedIndex === -1) {
      // add if not found
      item.requestQuantityInput = 1;
      this.$emit('update:picks', this.picks.concat(item));
    } else {
      // remove if found in the picked list
      this.$emit(
        'update:picks',
        this.picks.filter((f) => f.partId != item.partId)
      );
    }
  }

  data() {
    // this lets the undefined query! refs become reactive
    // https://class-component.vuejs.org/guide/class-component.html#data
    return {
      query: undefined,
      pagedParts: undefined,
      autoCompleteParts: undefined,
      orderAssets: undefined
    };
  }

  private sortChangedHandler(sort: SortChangedEvent) {
    // this will trigger the watcher, so we don't need to call it explicitly.
    this.query = {
      ...this.query,
      sortColumn: sort.column,
      sortDirection: sort.direction === 1 ? 'ascend' : 'descend'
    };
  }

  private paginationChangeHandler(pageNumber: number) {
    this.pageNumber = pageNumber;

    // update the page number, and fetch the update.
    this.fetchParts();
  }

  @Watch('query', { deep: true })
  private async queryChanged(val: ApiPartsGetRequest) {
    if (val) {
      // if the query changed, we reset the page number to page 1.
      this.pageNumber = 1;
      // fetch the parts list for the updated query parameters.
      this.fetchParts();
    }
  }

  @Watch('autoCompleteQuery', { immediate: true })
  private async autoCompleteQueryChanged(val: string) {
    const query = {
      partCode: val,
      propertyId: this.order.propertyId || '',
      skip: 0,
      take: 250,
      sortColumn: 'partCode',
      sortDirection: 'ascend'
    };
    try {
      this.autoCompleteParts = await this.api.apiPartsGet(query);
    } catch {
      /* noop */
    }
  }

  private async fetchParts() {
    // define a local copy of the query object, to override a few properties
    // like skip/take and sort, so those can trigger updates when changed.
    const query = {
      ...this.query,
      propertyId: this.order.propertyId || '',
      // calculate our skip based on the current page number
      skip: (this.query.take || 25) * (this.pageNumber - 1) + 1,
      take: this.query.take ? this.query.take : this.internalTake,
      sortColumn: this.query.sortColumn !== '' ? this.query.sortColumn : '',
      sortDirection:
        this.query.sortDirection !== '' ? this.query.sortDirection : 'ascend'
    };
    try {
      this.pagedParts = await this.api.apiPartsGet(query);
    } catch (error) {
      handleApiError(error);
    }
  }

  async onScannedHandler(barcode: string) {
    try {
      if (barcode && barcode !== '') {
        const partResult = await this.api.apiPartsGet({
          cPartBarCode: barcode
        });
        if (partResult && partResult.data && partResult.data.length === 1) {
          this.$emit('update:picks', this.picks.concat(partResult.data[0]));
        }
      }
    } catch (error) {
      handleApiError(error);
    }
  }

  private async mounted() {
    // must specify properties on objects that are to be reactive even if blank
    // see also -- https://stackoverflow.com/a/46667367/86860
    this.query = {
      partCode: '',
      sortColumn: 'partCode',
      sortDirection: 'ascend',
      skip: 0,
      take: this.internalTake
    };
    this.fetchParts();
  }
}
